import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BOT_LEVELS, PRESETS, createGame, validateRuleset, type Color, type Ruleset, type TimeControl } from '@ic/engine';
import { Board } from '../components/Board';
import { ChoiceCards, Stepper, Toggle } from '../components/Controls';
import { Icon } from '../components/Icons';
import { PieceIcon } from '../components/Pieces';
import { LoaderOverlay } from '../components/Loader';
import { onMessage, send } from '../lib/socket';
import { morphTarget, navigateMorph, useArrival } from '../lib/transition';
import { activeRuleset, persisted, useStore } from '../lib/store';
import { auth } from '../lib/auth';
import { FREE_GAMES, useGuestQuota } from '../lib/guest';
import { canPlay, publishedModes } from '../lib/modes';

export const TIME_CONTROLS: { group: string; items: TimeControl[] }[] = [
  { group: 'Bullet', items: [{ initial: 60, increment: 0 }, { initial: 120, increment: 1 }] },
  { group: 'Blitz', items: [{ initial: 180, increment: 0 }, { initial: 180, increment: 2 }, { initial: 300, increment: 0 }] },
  { group: 'Rapid', items: [{ initial: 600, increment: 0 }, { initial: 900, increment: 10 }, { initial: 1800, increment: 0 }] },
];

export const tcLabel = (tc: TimeControl) => (tc.initial === 0 ? 'Unlimited' : `${tc.initial >= 60 ? tc.initial / 60 : tc.initial + 's'} | ${tc.increment}`);
export const tcParam = (tc: TimeControl) => `${tc.initial}+${tc.increment}`;
export function parseTc(s: string | null): TimeControl | null {
  if (!s) return null;
  // '+' arrives as a space when read back from the query string.
  const [a, b] = s.split(/[+ ]/).map(Number);
  return Number.isFinite(a) ? { initial: a, increment: Number.isFinite(b) ? b : 0 } : null;
}

export type Mode = 'online' | 'friend' | 'bot' | 'local' | 'four';
/** Last choices on the Play page (also preset by the shortcuts on Home). */
export const prefs = persisted('ic.playPrefs', { mode: 'bot' as Mode, level: 3, color: 'w' as Color | 'random', rated: true, fourOnline: true });

const sameTc = (a: TimeControl, b: TimeControl) => a.initial === b.initial && a.increment === b.increment;

function Thumb({ rs }: { rs: Ruleset }) {
  const s = useMemo(() => createGame(rs), [rs]);
  return <div className="thumb"><Board state={s} orientation="w" /></div>;
}

function Bars({ n }: { n: number }) {
  return <span className="bars">{BOT_LEVELS.map((b) => <i key={b.level} className={b.level <= n ? 'lit' : ''} style={{ height: 3 + b.level * 1.6 }} />)}</span>;
}

function SidePicker({ value, onChange }: { value: Color | 'random'; onChange: (c: Color | 'random') => void }) {
  return (
    <div className="side-picker" role="radiogroup">
      {([['w', 'White'], ['random', 'Random'], ['b', 'Black']] as const).map(([c, label]) => (
        <button key={c} type="button" role="radio" aria-checked={value === c} className={value === c ? 'on' : ''} onClick={() => onChange(c)}>
          {c === 'random' ? (
            <span className="split-piece">
              <PieceIcon icon="king" color="w" set="classic" className="left" />
              <PieceIcon icon="king" color="b" set="classic" className="right" />
            </span>
          ) : <PieceIcon icon="king" color={c} set="classic" />}
          {label}
        </button>
      ))}
    </div>
  );
}

export function Play() {
  const chosen = useStore(activeRuleset);
  const { admin } = useStore(auth);
  const modes = useStore(publishedModes);
  // Custom rules (Rule Editor, share links) are for admins; everybody else plays a preset or published mode.
  const rs = canPlay(chosen) ? chosen : PRESETS[0];
  useEffect(() => { if (rs !== chosen) activeRuleset.set(JSON.parse(JSON.stringify(rs))); }, [rs, chosen]);
  const p = useStore(prefs);
  const setP = (patch: Partial<typeof p>) => prefs.set({ ...p, ...patch });
  const nav = useNavigate();
  const [tc, setTc] = useState<TimeControl>(rs.rules.clock);
  const [seeking, setSeeking] = useState(false);
  const [seek4, setSeek4] = useState<{ waiting: number; botsInMs: number; at: number } | null>(null);
  const [tick, setTick] = useState(0);
  const [err, setErr] = useState('');
  const online = navigator.onLine;
  const errors = validateRuleset(rs).filter((i) => i.level === 'error');
  const isPreset = [...PRESETS, ...modes].some((x) => x.id === rs.id);
  const isCustomTc = tc.initial !== 0 && !TIME_CONTROLS.some((g) => g.items.some((i) => sameTc(i, tc)));
  const [showCustom, setShowCustom] = useState(isCustomTc);
  const arrival = useArrival();

  useEffect(() => onMessage((m) => {
    if (m.t === 'gameStart' || m.t === 'inviteCreated') { setSeeking(false); nav(`/game/${m.gameId}`); }
    if (m.t === 'seeking4') setSeek4({ waiting: m.waiting, botsInMs: m.botsInMs, at: Date.now() });
    if (m.t === 'game4Start') { setSeek4(null); nav(`/four/${m.gameId}`); }
    if (m.t === 'error') { setErr(m.message); setSeeking(false); }
  }), [nav]);
  useEffect(() => () => { if (seeking) send({ t: 'cancelSeek' }); }, [seeking]);
  const searching4 = !!seek4;
  useEffect(() => () => { if (searching4) send({ t: 'cancelSeek4' }); }, [searching4]);
  useEffect(() => { if (!seek4) return; const t = setInterval(() => setTick((x) => x + 1), 500); return () => clearInterval(t); }, [seek4]);

  const mode: Mode = !online && (p.mode === 'online' || p.mode === 'friend') ? 'bot' : p.mode;
  const four4Online = mode === 'four' && p.fourOnline && online;
  // Guests: online play needs an account, offline games are limited.
  const quota = useGuestQuota();
  const needsAccount = quota.guest && (mode === 'online' || mode === 'friend' || four4Online);
  const outOfFree = quota.guest && !needsAccount && quota.left === 0;
  const pickColor = (): Color => (p.color === 'random' ? (Math.random() < 0.5 ? 'w' : 'b') : p.color);
  const bot = BOT_LEVELS[p.level - 1];

  function choosePreset(x: Ruleset) {
    activeRuleset.set(JSON.parse(JSON.stringify(x)));
    setTc(x.rules.clock);
    setShowCustom(false);
  }

  function start() {
    setErr('');
    if (mode === 'four') {
      if (four4Online) { setSeek4({ waiting: 1, botsInMs: 60_000, at: Date.now() }); send({ t: 'seek4', tc }); }
      else nav('/four/local');
    }
    else if (mode === 'online') { setSeeking(true); send({ t: 'seek', tc, rated: p.rated, ruleset: rs }); }
    else if (mode === 'friend') send({ t: 'createInvite', tc, rated: false, ruleset: rs, color: p.color });
    else {
      const url = mode === 'bot' ? `/play/bot?level=${p.level}&color=${pickColor()}&tc=${tcParam(tc)}` : `/play/local?tc=${tcParam(tc)}`;
      navigateMorph(nav, url, document.querySelector<HTMLElement>('.preset-tile.on .thumb'), 'board');
    }
  }

  const modeText = {
    online: `Online${p.rated ? ', rated' : ', casual'}`,
    friend: 'Invite a friend by link',
    bot: `vs ${bot.name} bot as ${p.color === 'random' ? 'random side' : p.color === 'w' ? 'White' : 'Black'}`,
    local: 'Pass & play on this device',
    four: four4Online ? '4 players online, bots fill free seats after a minute' : '4 players: you against three computers',
  }[mode];
  const startLabel = { online: 'Find opponent', friend: 'Create invite link', bot: 'Start game', local: 'Start game', four: four4Online ? 'Find players' : 'Start game' }[mode];

  return (
    <div className={`page play-page ${arrival ? 'page-enter' : ''}`}>
      <div className="setup-head">
        <Link to="/play" className="back-link"><Icon name="prev" size={14} /> Start</Link>
        <h1>New game</h1>
      </div>

      <section className="play-step">
        <div className="step-head"><span className="step-no">1</span><h2>How do you want to play?</h2></div>
        <ChoiceCards<Mode>
          value={mode}
          onChange={(m) => setP({ mode: m })}
          options={[
            { value: 'bot', title: 'Computer', desc: `${BOT_LEVELS.length} strength levels. Works offline.`, visual: <Icon name="cpu" size={22} /> },
            { value: 'online', title: 'Online', desc: 'Get matched with the same rules and clock.', visual: <Icon name="globe" size={22} />, disabled: !online, badge: !online && <span className="tag">offline</span> },
            { value: 'friend', title: 'Friend', desc: 'Send a private link, they join instantly.', visual: <Icon name="link" size={22} />, disabled: !online, badge: !online && <span className="tag">offline</span> },
            { value: 'local', title: 'Pass & play', desc: 'Two players, one device.', visual: <Icon name="users" size={22} /> },
            { value: 'four', title: '4 players', desc: 'Free for all with Green and Yellow.', visual: <span className="four-dots"><i className="w" /><i className="g" /><i className="b" /><i className="y" /></span> },
          ].map((o) => ({ ...o, value: o.value as Mode, ...morphTarget(arrival === 'mode' && o.value === mode) }))}
        />
      </section>

      {mode === 'four' && (
        <section className="play-step">
          <div className="step-head"><span className="step-no">2</span><h2>Four players</h2><span className="step-aside">14×14 cross board, classic pieces</span></div>
          <ChoiceCards<'online' | 'offline'>
            value={four4Online ? 'online' : 'offline'}
            onChange={(v) => setP({ fourOnline: v === 'online' })}
            options={[
              { value: 'online', title: 'Online', desc: 'Matched with other players. After one minute, bots take the free seats.', visual: <Icon name="globe" size={22} />, disabled: !online },
              { value: 'offline', title: 'Against computers', desc: 'You play White against three bots. Works offline.', visual: <Icon name="cpu" size={22} /> },
            ]}
          />
          <p className="muted small">Turns go clockwise: White, Green, Black, Yellow. Whoever has no move left (checkmate or stalemate) is out and their pieces turn grey. The last player standing wins.</p>
        </section>
      )}

      {mode !== 'four' && <section className="play-step">
        <div className="step-head">
          <span className="step-no">2</span><h2>Rules</h2>
          <span className="step-aside">{rs.name} · {rs.board.width}×{rs.board.height}</span>
        </div>
        <div className="preset-strip">
          {!isPreset && (
            <button type="button" className="preset-tile on" title={rs.description}>
              <Thumb rs={rs} />
              <b>{rs.name}</b>
              <span className="tile-desc">Your custom rules</span>
              <span className="choice-check" aria-hidden="true"><Icon name="check" size={12} /></span>
            </button>
          )}
          {[...PRESETS, ...modes].map((x) => (
            <button type="button" key={x.id} {...(() => { const m = morphTarget(arrival === 'preset' && rs.id === x.id); return { style: m.style, className: `preset-tile ${rs.id === x.id ? 'on' : ''} ${m.className}` }; })()} onClick={() => choosePreset(x)} title={x.description}>
              <Thumb rs={x} />
              <b>{x.name}</b>
              <span className="tile-desc">{x.description}</span>
              <span className="choice-check" aria-hidden="true"><Icon name="check" size={12} /></span>
            </button>
          ))}
          {admin && (
            <Link to="/editor" className="preset-tile edit">
              <Icon name="dig" size={26} />
              <b>Make your own</b>
              <span className="tile-desc">Open the Rule Editor</span>
            </Link>
          )}
        </div>
        {errors.length > 0 && <div className="notice error">This ruleset can't be played yet: {errors[0].message}</div>}
      </section>}

      <section className="play-step">
        <div className="step-head">
          <span className="step-no">3</span><h2>Time control</h2>
          <span className="step-aside">{tcLabel(tc)}{tc.initial > 0 ? ' (minutes | increment)' : ''}</span>
        </div>
        <div className="tc-groups-row">
          {TIME_CONTROLS.map((g) => (
            <div key={g.group}>
              <div className="tc-group-label">{g.group}</div>
              <div className="tc-chips">
                {g.items.map((i) => (
                  <button type="button" key={tcParam(i)} className={`tc-chip ${sameTc(i, tc) && !showCustom ? 'on' : ''}`} onClick={() => { setTc(i); setShowCustom(false); }}>
                    <b>{i.initial / 60}+{i.increment}</b><span>{i.initial / 60} min{i.increment ? `, +${i.increment}s` : ''}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <div className="tc-group-label">Other</div>
            <div className="tc-chips">
              <button type="button" className={`tc-chip ${tc.initial === 0 && !showCustom ? 'on' : ''}`} onClick={() => { setTc({ initial: 0, increment: 0 }); setShowCustom(false); }}>
                <b>∞</b><span>No clock</span>
              </button>
              <button type="button" className={`tc-chip ${showCustom ? 'on' : ''}`} onClick={() => { setShowCustom(true); if (tc.initial === 0) setTc({ initial: 600, increment: 5 }); }}>
                <b><Icon name="clock" size={16} /></b><span>Custom</span>
              </button>
            </div>
          </div>
        </div>
        {showCustom && (
          <div className="tc-custom-row">
            <label>Minutes <Stepper value={Math.max(1, Math.round(tc.initial / 60))} min={1} max={180} onChange={(m) => setTc({ ...tc, initial: m * 60 })} /></label>
            <label>Increment <Stepper value={tc.increment} min={0} max={60} suffix="s" onChange={(i) => setTc({ ...tc, increment: i })} /></label>
          </div>
        )}
      </section>

      {(mode === 'bot' || mode === 'online' || mode === 'friend') && (
        <section className="play-step">
          <div className="step-head"><span className="step-no">4</span><h2>Options</h2></div>
          <div className="options-grid">
            {mode === 'bot' && (
              <div className="card">
                <h3>Bot strength</h3>
                <div className="strength" role="radiogroup">
                  {BOT_LEVELS.map((b) => (
                    <button key={b.level} type="button" role="radio" aria-checked={p.level === b.level} className={p.level === b.level ? 'on' : ''}
                      onClick={() => setP({ level: b.level })} title={`Searches ${b.depth} move${b.depth > 1 ? 's' : ''} ahead`}>
                      <Bars n={b.level} /><b>{b.name}</b>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(mode === 'bot' || mode === 'friend') && (
              <div className="card">
                <h3>Your side</h3>
                <SidePicker value={p.color} onChange={(color) => setP({ color })} />
              </div>
            )}
            {mode === 'online' && (
              <div className="card">
                <Toggle checked={p.rated} onChange={(rated) => setP({ rated })} label="Rated game"
                  hint="Changes your Elo. Needs an account; guests always play casual." />
              </div>
            )}
          </div>
        </section>
      )}

      {seek4 && (
        <LoaderOverlay label="Looking for players…">
          <div className="muted small">{seek4.waiting} of 4 waiting{tick >= 0 && seek4.botsInMs > 0 ? ` · bots join in ${Math.max(0, Math.ceil((seek4.botsInMs - (Date.now() - seek4.at)) / 1000))}s` : ' · starting with bots…'}</div>
          <button className="btn secondary" onClick={() => { send({ t: 'cancelSeek4' }); setSeek4(null); }}>Cancel</button>
        </LoaderOverlay>
      )}

      {seeking && (
        <LoaderOverlay label="Looking for an opponent…">
          <div className="muted small">{rs.name} · {tcLabel(tc)}{p.rated ? ' · rated' : ''}</div>
          <button className="btn secondary" onClick={() => { send({ t: 'cancelSeek' }); setSeeking(false); }}>Cancel</button>
        </LoaderOverlay>
      )}

      <div className="start-bar">
        <div className="start-summary">
          <b>{modeText}</b>
          <span>{needsAccount ? 'Online games need an account.' : outOfFree ? `You played your ${FREE_GAMES} free games.` : `${rs.name} · ${tcLabel(tc)}${quota.guest ? ` · ${quota.left} of ${FREE_GAMES} free games left` : ''}`}{err && ` · ${err}`}</span>
        </div>
        <div className="start-actions">
          {needsAccount || outOfFree ? (
            <>
              <Link to="/login?next=/play/setup" className="btn secondary">Log in</Link>
              <Link to="/login?mode=signup&next=/play/setup" className="btn primary xl">Sign up to play <Icon name="arrow" size={18} /></Link>
            </>
          ) : seeking ? (
            <button className="btn danger" onClick={() => { send({ t: 'cancelSeek' }); setSeeking(false); }}><span className="spinner" /> Searching… cancel</button>
          ) : (
            <button className="btn primary xl" disabled={mode !== 'four' && !!errors.length} onClick={start}>{startLabel} <Icon name="arrow" size={18} /></button>
          )}
        </div>
      </div>
    </div>
  );
}
